import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FaEllipsisH } from 'react-icons/fa';
import * as htmlToImage from 'html-to-image';
import LoaderImage from '@assets/loader.gif';
import './ExportButton.scss';

function useComponentVisible(initialIsVisible: boolean) {
    const [isComponentVisible, setIsComponentVisible] = useState(
        initialIsVisible
    );
    const ref = useRef<any>(null);

    const handleHideDropdown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            setIsComponentVisible(false);
        }
    };

    const handleClickOutside = (event:MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target)) {
            setTimeout(function () {
                setIsComponentVisible(false);
            }, 100);
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleHideDropdown, true);
        document.addEventListener("click", handleClickOutside, true);
        return () => {
            document.removeEventListener("keydown", handleHideDropdown, true);
            document.removeEventListener("click", handleClickOutside, true);
        };
    });

    return { ref, isComponentVisible, setIsComponentVisible };
}

export type ExportButtonType = {
    className?: string,
    countryName?: string,
    exportDOMId?: string,
    urlAPI?: any,
    urlDataAPI?: any
};

const ExportButton: FC<ExportButtonType> = ({ className, countryName, exportDOMId, urlAPI, urlDataAPI }) => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleExportAsPNGImage = useCallback(() => {
        if (exportDOMId) {
            setIsComponentVisible(false);
            const dom = document.getElementById(exportDOMId);
            if (dom) {
                htmlToImage.toPng(dom)
                    .then(function (dataUrl) {
                        var link = document.createElement('a');
                        link.download = exportDOMId + (countryName ? "-" + countryName?.replaceAll(" ", "") : "") + '.png';
                        link.href = dataUrl;
                        link.click();
                    });
            }
        }
    }, [exportDOMId, countryName, setIsComponentVisible]);

    const handleExportAsJPGImage = useCallback(() => {
        if (exportDOMId) {
            setIsComponentVisible(false);
            const dom = document.getElementById(exportDOMId);
            if (dom) {
                htmlToImage.toJpeg(dom)
                    .then(function (dataUrl) {
                        var link = document.createElement('a');
                        link.download = exportDOMId + (countryName ? "-" + countryName?.replaceAll(" ", "") : "") + '.jpg';
                        link.href = dataUrl;
                        link.click();
                    });
            }
        }
    }, [exportDOMId, countryName, setIsComponentVisible]);

    const handleExportAsXLSXorCSV = async (exportType: string) => {
        var dataAPI = { ...urlDataAPI };
        dataAPI["exportType"] = exportType;
        setIsLoading(true);
        const [err, res] = await urlAPI(dataAPI);
        if (err) console.warn(err);
        if (res) {
            const url = window.URL.createObjectURL(new Blob([res]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", exportDOMId + (countryName ? "-" + countryName.replaceAll(" ", "") : "") + "." + exportType);
            document.body.appendChild(link);
            link.click();
        }
        setIsLoading(false);
    };

    return (
        <div className={"export-dropdown-container " + className} >
            {!isLoading ?  
                <>
                    <div className="export-dropdown" ref={ref} onClick={() => setIsComponentVisible(!isComponentVisible)}>
                        <FaEllipsisH />
                    </div>
                    {isComponentVisible && (
                        <ul>
                            <li className="default">Export All</li>
                            {exportDOMId &&
                                <>
                                    <li className="separator"><hr /></li>
                                    <li className="clickable" onClick={handleExportAsPNGImage}>PNG <span>Image</span></li>
                                    <li className="clickable" onClick={handleExportAsJPGImage}>JPG <span>Image</span></li>
                                </>
                            }
                            {urlAPI && urlDataAPI &&
                                <>
                                    <li className="separator"><hr /></li>
                                    {/*<li className="clickable" onClick={() => handleExportAsXLSXorCSV("csv")}>CSV <span>Data</span></li>*/}
                                    <li className="clickable" onClick={() => handleExportAsXLSXorCSV("xlsx")}>XLSX <span>Data</span></li>
                                </>
                            }
                        </ul>
                    )}       
                </>
                :
                <div className="float-end"><img src={LoaderImage} alt="" className="mx-auto d-block" style={{ width: 30 }} /></div>
            }
        </div>
    );
};

export default ExportButton;